import { huntsCollection, codeAttemptLogsCollection } from "@/firebaseConfig";
import { Hunt } from "@/models/hunt";
import isOnline from "is-online";
import {
  query,
  getDoc,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ReferenceMetaData } from "./audio.service";
import { Challenge } from "@/models/challenge";

type RawHunt = { code: unknown; codeCount?: number };

type CodeAttemptFailureResult = "INVALID_CODE" | "OFFLINE" | "ERROR" | "NO_CODES_LEFT";

/**
 * Fire-and-forget diagnostic log for failed code-entry attempts. Never awaited and never
 * throws - a logging failure must not block or surface to the player. This is currently
 * the only durable record of code-entry failures anywhere (console.error is invisible for
 * real users, and there's no other analytics/error-reporting wired into this app), added
 * after a live event where ~70 users shared one code and some intermittently saw "Wrong
 * access code" with no way afterward to tell what actually happened.
 */
function logCodeAttemptFailure(
  code: string,
  result: CodeAttemptFailureResult,
  huntsScanned: number
): void {
  addDoc(codeAttemptLogsCollection, {
    code,
    result,
    huntsScanned,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
    createdAt: serverTimestamp(),
  }).catch((error) => {
    console.error("failed to log code attempt failure: ", error);
  });
}

// Handles both old string[] format and new {code,codeCount}[] format
function normalizeCodeEntry(rawHunt: unknown, codeLower: string): { code: string; codeCount: number } | undefined {
  const hunt = rawHunt as RawHunt;
  const codes = hunt.code;
  const fallbackCount = hunt.codeCount ?? 1;
  if (!Array.isArray(codes)) {
    if (typeof codes === "string" && codes.toLowerCase() === codeLower) {
      return { code: codes, codeCount: fallbackCount };
    }
    return undefined;
  }
  for (const c of codes) {
    if (!c) continue;
    if (typeof c === "string") {
      if (c.toLowerCase() === codeLower) return { code: c, codeCount: fallbackCount };
    } else if (typeof c.code === "string" && c.code.toLowerCase() === codeLower) {
      return c;
    }
  }
  return undefined;
}

const HuntService = {
  // TODO implement a sensible limit to this query - should not being getting all from DB
  getAll: async () => {
    const hunts: Hunt[] = [];
    try {
      const q = query(huntsCollection);
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((docSnapshot) => {
        const hunt = docSnapshot.data() as Hunt;
        hunt.id = docSnapshot.id;
        hunts.push(hunt);
      });
    } catch (error) {
      console.error(error);
    }
    return hunts;
  },
  getHuntByCode: async (code: string) => {
    if (!(await isOnline())) {
      logCodeAttemptFailure(code, "OFFLINE", 0);
      return "OFFLINE";
    }
    try {
      const codeLower = code.trim().toLowerCase();
      if (!codeLower) {
        logCodeAttemptFailure(code, "INVALID_CODE", 0);
        return "INVALID_CODE";
      }

      // Fetch all hunts and find matching code (Firestore cannot query array of objects easily)
      const q = query(huntsCollection);
      const querySnapshot = await getDocs(q);

      for (const docSnapshot of querySnapshot.docs) {
        const hunt = docSnapshot.data() as Hunt;
        hunt.id = docSnapshot.id;

        const codeEntry = normalizeCodeEntry(hunt, codeLower);
        if (codeEntry) {
          if ((codeEntry.codeCount ?? 0) < 1) {
            logCodeAttemptFailure(code, "NO_CODES_LEFT", querySnapshot.size);
            return "NO_CODES_LEFT";
          }
          (hunt as Hunt & { usedCode?: string }).usedCode = codeLower;
          return hunt;
        }
      }
      logCodeAttemptFailure(code, "INVALID_CODE", querySnapshot.size);
      return "INVALID_CODE";
    } catch (error) {
      console.error(error);
      logCodeAttemptFailure(code, "ERROR", 0);
      return "ERROR";
    }
  },
  /**
   * Returns whether any other hunt (not excludeHuntId) already uses one of the given codes.
   * Comparison matches getHuntByCode: trim + case-insensitive on the code string.
   */
  findConflictingAccessCodeForHunt: async (
    codes: string[],
    excludeHuntId: string
  ): Promise<
    | null
    | { code: string; otherHuntId: string; otherHuntName: string }
    | "OFFLINE"
    | "ERROR"
  > => {
    if (!(await isOnline())) {
      return "OFFLINE";
    }
    const trimmed = codes.map((c) => c.trim()).filter(Boolean);
    if (trimmed.length === 0) {
      return null;
    }
    const normalizedSet = new Set(
      trimmed.map((c) => c.toLowerCase())
    );
    try {
      const q = query(huntsCollection);
      const querySnapshot = await getDocs(q);
      for (const docSnapshot of querySnapshot.docs) {
        if (docSnapshot.id === excludeHuntId) {
          continue;
        }
        const hunt = docSnapshot.data() as Hunt;
        const huntId = docSnapshot.id;
        if (!Array.isArray(hunt.code)) {
          continue;
        }
        for (const entry of hunt.code) {
          if (
            !entry ||
            typeof entry.code !== "string" ||
            !entry.code.trim()
          ) {
            continue;
          }
          const entryLower = entry.code.trim().toLowerCase();
          if (normalizedSet.has(entryLower)) {
            const displayCode =
              trimmed.find((c) => c.toLowerCase() === entryLower) ??
              entry.code.trim();
            return {
              code: displayCode,
              otherHuntId: huntId,
              otherHuntName: hunt.name || "",
            };
          }
        }
      }
      return null;
    } catch (error) {
      console.error(error);
      return "ERROR";
    }
  },
  getHuntById: async (id: string) => {
    if (!(await isOnline())) {
      return "OFFLINE";
    }
    try {
      const huntDocRef = doc(huntsCollection, id);
      const docSnap  = await getDoc(huntDocRef);

      if(!docSnap.exists()) {
        return "INVALID_CODE";
      }

      return (docSnap.data() as Hunt);
    } catch (error) {
      console.error(error);
      return "ERROR";
    }
  },
  setHuntById: async (id: string, hunt: Hunt) => {
    if (!id) {
    console.error("No hunt ID provided");
    return "ERROR";
  }
    try {
      const huntDocRef = doc(huntsCollection, id);
      await updateDoc(huntDocRef, {
        ...hunt,
        createdAt: hunt.createdAt, // Firestore requires Date to Timestamp
      });
      return "SUCCESS";
    } catch (error) {
      console.error(error);
      return "ERROR";
    }
  },
  addHunt: async (hunt: Hunt) => {
    try {
      const docRef = await addDoc(huntsCollection, hunt);
      return docRef.id;
    } catch (error) {
      console.error(error);
    }
    return "";
  },
  deleteHunt: async (hunt: Hunt) => {
    try {
      const huntDocRef = doc(huntsCollection, hunt.id);
      await deleteDoc(huntDocRef);
    } catch (error) {
      console.error(error);
    }
  },
  // updateAllChallenges can not be used yet due to the current architecture, until the Challenges[] and Hunt are decoupled in Firestore, keeping this func as it may be useful later
  updateAllChallenges: async (
    huntObj: Hunt,
    challenges: Challenge[],
    aa: ReferenceMetaData,
    step: number): Promise<Challenge[]> => {
    try {
      const allChallenges = challenges;
      const modifiedHuntChallenge: Challenge = {
        audio: {
          name: aa.name,
          downloadUrl: aa.downloadUrl,
          path: aa.fullPath,
          mediaFileId: aa.docId || '',
        },
        ...challenges[step],
      }
      allChallenges[step] = modifiedHuntChallenge;
      const huntDocRef = doc(huntsCollection, huntObj.id);
      await updateDoc(huntDocRef, {
        challenges: allChallenges,
      });
      return allChallenges;
    } catch (error) {
      console.error(error);
      return [];
    }
  },
};

export default HuntService;

export { HuntService };
